import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import "./Paramrecrut.css";

const SECTIONS = [
    {
        id: "participatif",
        title: "Recrutement participatif",
        type: "checkbox-list",
        items: [
            "Cooptation",
            "Recommandation",
            "Chasseur de Tête",
            "Partenaire Académiques",
            "Réseaux Sociaux",
        ],
    },
    {
        id: "seuil",
        title: "Seuil d'entretien",
        type: "checkbox-list",
        items: ["Entretien Technique", "Entretien RH", "Global"],
    },
    {
        id: "fiche",
        title: "Fiche appréciation",
        type: "inline-checkboxes",
        items: ["Entretien Technique", "Entretien RH"],
    },
    {
        id: "emails",
        title: "E-mails",
        type: "emails",
    },
    {
        id: "prerequis",
        title: "Pré-requis",
        type: "requirements",
    },
    {
        id: "autre",
        title: "Autre",
        type: "other",
    },
    {
        id: "matching",
        title: "Paramètrage Matching",
        type: "matching",
    },
];

const DEFAULT_MATCHING = {
    education: 40,
    experience: 40,
    technicalSkills: 15,
    softSkills: 0,
    languages: 5,
};

function CheckboxOption({ label, checked, onChange }) {
    return (
        <label className="pr-option">
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span>{label}</span>
        </label>
    );
}

function RadioOption({ name, label, checked, onChange }) {
    return (
        <label className="pr-radio">
            <input type="radio" name={name} checked={checked} onChange={onChange} />
            <span>{label}</span>
        </label>
    );
}

function SaveButton() {
    return (
        <div className="pr-save-row">
            <button type="button" className="pr-save-btn">
                Enregistrer
            </button>
        </div>
    );
}

export default function Paramrecrut() {
    const [openSection, setOpenSection] = useState("participatif");
    const [checkedValues, setCheckedValues] = useState({});
    const [emailMode, setEmailMode] = useState("instantane");
    const [joinJobDescription, setJoinJobDescription] = useState("");
    const [salaryType, setSalaryType] = useState("");
    const [signatureEnabled, setSignatureEnabled] = useState(false);
    const [deadlineValue, setDeadlineValue] = useState("");
    const [deadlineUnit, setDeadlineUnit] = useState("Jours");
    const [matching, setMatching] = useState(DEFAULT_MATCHING);

    const toggleSection = (sectionId) => {
        setOpenSection((current) => (current === sectionId ? "" : sectionId));
    };

    const toggleCheck = (sectionId, label) => {
        const key = `${sectionId}-${label}`;
        setCheckedValues((current) => ({
            ...current,
            [key]: !current[key],
        }));
    };

    const totalMatching = useMemo(
        () => Object.values(matching).reduce((sum, value) => sum + Number(value), 0),
        [matching]
    );

    const renderCheckboxList = (section) => (
        <div className={`pr-options ${section.type === "inline-checkboxes" ? "pr-options-inline" : ""}`}>
            {section.items.map((item) => (
                <CheckboxOption
                    key={item}
                    label={item}
                    checked={!!checkedValues[`${section.id}-${item}`]}
                    onChange={() => toggleCheck(section.id, item)}
                />
            ))}
            <SaveButton />
        </div>
    );

    const renderEmails = () => (
        <div className="pr-form-block pr-email-block">
            <div className="pr-line-label">+ Envoi des emails :</div>
            <div className="pr-radio-row">
                <RadioOption
                    name="emailMode"
                    label="Manuel"
                    checked={emailMode === "manuel"}
                    onChange={() => setEmailMode("manuel")}
                />
                <RadioOption
                    name="emailMode"
                    label="Instantané"
                    checked={emailMode === "instantane"}
                    onChange={() => setEmailMode("instantane")}
                />
                <RadioOption
                    name="emailMode"
                    label="Différé"
                    checked={emailMode === "differe"}
                    onChange={() => setEmailMode("differe")}
                />
            </div>
            <SaveButton />
        </div>
    );

    const renderRequirements = () => {
        const salaryDocuments = [
            "Les bulletins de paie",
            "Attestation de travail",
            "Pièce d'identité",
            "Autres",
        ];
        const contractDocuments = [
            "Copier d'aquisition la dimission",
            "CIN",
            "Radio",
            "N.A CNSS",
            "Autres",
            "Préavis",
        ];

        return (
            <div className="pr-form-block pr-requirements">
                <div className="pr-line-label">+ Offre salariale :</div>
                <div className="pr-sub-options">
                    {salaryDocuments.map((item) => (
                        <CheckboxOption
                            key={item}
                            label={item}
                            checked={!!checkedValues[`prerequis-${item}`]}
                            onChange={() => toggleCheck("prerequis", item)}
                        />
                    ))}
                </div>

                <div className="pr-line-label">+ Contrat de travail :</div>
                <div className="pr-sub-options">
                    {contractDocuments.map((item) => (
                        <CheckboxOption
                            key={item}
                            label={item}
                            checked={!!checkedValues[`prerequis-${item}`]}
                            onChange={() => toggleCheck("prerequis", item)}
                        />
                    ))}
                </div>
                <SaveButton />
            </div>
        );
    };

    const renderOther = () => (
        <div className="pr-form-block pr-other">
            <div className="pr-form-row">
                <div className="pr-line-label">+ Joindre la descriptif de poste (1er entretien planifié) :</div>
                <div className="pr-radio-row">
                    <RadioOption
                        name="jobDescription"
                        label="Oui"
                        checked={joinJobDescription === "oui"}
                        onChange={() => setJoinJobDescription("oui")}
                    />
                    <RadioOption
                        name="jobDescription"
                        label="Non"
                        checked={joinJobDescription === "non"}
                        onChange={() => setJoinJobDescription("non")}
                    />
                </div>
            </div>

            <div className="pr-form-row">
                <div className="pr-line-label">+ Offre salariale :</div>
                <div className="pr-radio-row">
                    <RadioOption
                        name="salaryType"
                        label="Brut"
                        checked={salaryType === "brut"}
                        onChange={() => setSalaryType("brut")}
                    />
                    <RadioOption
                        name="salaryType"
                        label="Net"
                        checked={salaryType === "net"}
                        onChange={() => setSalaryType("net")}
                    />
                </div>
            </div>

            <div className="pr-line-label">+ Signature électronique :</div>
            <div className="pr-sub-options pr-other-check">
                <CheckboxOption
                    label="Autorisée"
                    checked={signatureEnabled}
                    onChange={() => setSignatureEnabled((current) => !current)}
                />
            </div>

            <div className="pr-line-label">+ Délai après l'envoi de la lettre d'engagement :</div>
            <div className="pr-deadline-box">
                <input
                    aria-label="Délai"
                    value={deadlineValue}
                    onChange={(event) => setDeadlineValue(event.target.value)}
                />
                <label>
                    <span>Unité</span>
                    <select value={deadlineUnit} onChange={(event) => setDeadlineUnit(event.target.value)}>
                        <option>Jours</option>
                        <option>Semaines</option>
                        <option>Mois</option>
                    </select>
                </label>
            </div>
            <SaveButton />
        </div>
    );

    const renderMatching = () => {
        const rows = [
            ["education", "Education"],
            ["experience", "Expérience"],
            ["technicalSkills", "Compétences techniques"],
            ["softSkills", "Soft skills"],
            ["languages", "Langues"],
        ];

        return (
            <div className="pr-matching-wrap">
                <div className="pr-matching-card">
                    <h2>Définir les paramètres d'évaluation</h2>
                    {rows.map(([key, label]) => (
                        <label className="pr-slider-row" key={key}>
                            <span>
                                {label} <strong>{matching[key]}%</strong>
                            </span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={matching[key]}
                                onChange={(event) =>
                                    setMatching((current) => ({
                                        ...current,
                                        [key]: Number(event.target.value),
                                    }))
                                }
                            />
                        </label>
                    ))}
                    <div className={`pr-matching-total ${totalMatching === 100 ? "is-valid" : ""}`}>
                        Total: {totalMatching}%
                    </div>
                    <button type="button" className="pr-matching-save">
                        Save Parameters
                    </button>
                </div>
            </div>
        );
    };

    const renderContent = (section) => {
        if (section.type === "checkbox-list" || section.type === "inline-checkboxes") {
            return renderCheckboxList(section);
        }
        if (section.type === "emails") return renderEmails();
        if (section.type === "requirements") return renderRequirements();
        if (section.type === "other") return renderOther();
        return renderMatching();
    };

    return (
        <main className="pr-page">
            <h1>Paramétrage du recrutement</h1>

            <section className="pr-accordion" aria-label="Paramétrage du recrutement">
                {SECTIONS.map((section) => {
                    const isOpen = openSection === section.id;
                    const Icon = isOpen ? ChevronDown : ChevronRight;

                    return (
                        <article className={`pr-section ${isOpen ? "is-open" : ""}`} key={section.id}>
                            <button
                                type="button"
                                className="pr-section-header"
                                onClick={() => toggleSection(section.id)}
                                aria-expanded={isOpen}
                            >
                                <Icon size={18} aria-hidden="true" />
                                <span>{section.title}</span>
                            </button>
                            {isOpen && <div className="pr-section-content">{renderContent(section)}</div>}
                        </article>
                    );
                })}
            </section>
        </main>
    );
}
